import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import { useSelector, useDispatch } from "react-redux";
import { getStaffQuarterPermissions } from "../../../../core/data/redux/staffQuarterSlice";

const DashboardTile = ({
  title,
  icon,
  count,
  link,
  state,
  colorClass = "text-primary",
}: {
  title: string;
  icon?: string;
  count?: string | number;
  link: string;
  state?: any;
  colorClass?: string;
}) => {
  return (
    <Link to={link} state={state} className="text-decoration-none col-xxl-2 col-xl-2 col-lg-3 col-md-4 col-sm-6 col-6 mb-3">
      <div
        className="card h-100 border-0 shadow-sm hover-card"
        style={{
          borderRadius: "16px",
          transition: "all 0.3s ease",
          cursor: "pointer",
          overflow: "hidden",
          backgroundColor: "#fff"
        }}
      >
        <div className="card-body d-flex flex-column align-items-center justify-content-center text-center p-3">

          {/* Icon Section - Large Relative to Card */}
          <div
            className={`d-flex align-items-center justify-content-center mb-2 rounded-circle`}
            style={{
              width: "48px",
              height: "48px",
              backgroundColor: "rgba(var(--bs-primary-rgb), 0.05)",
              border: "1px solid rgba(0,0,0,0.03)"
            }}
          >
            {icon && <i className={`ti ${icon} ${colorClass}`} style={{ fontSize: "24px" }}></i>}
          </div>

          <h6 className="fw-bold text-dark mb-0 w-100 px-1 text-truncate" title={title} style={{ fontSize: "12px", letterSpacing: "0.2px" }}>
            {title}
          </h6>

          {count !== undefined && (
            <div className="mt-2">
              <span className={`badge rounded-pill bg-light ${colorClass} border`} style={{ fontSize: "10px", padding: "3px 8px", fontWeight: "600" }}>
                {count} {count === 1 ? 'Item' : 'Items'}
              </span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};
const DashboardSection = ({ title, children }: { title: string; children: React.ReactNode }) => {
  return (
    <div className="mb-4">
      <h5 className="fw-bold text-secondary mb-3 ms-1" style={{ fontSize: "15px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {title}
      </h5>
      <div className="row">{children}</div>
    </div>
  );
};

const Overview: React.FC = () => {
  const routes = all_routes as any;
  const dispatch = useDispatch();

  const { permissions: sqPermissions, loading: sqLoading } = useSelector((state: any) => state.staffQuarterState || {});

  useEffect(() => {
    if (sqLoading === "idle" || !sqPermissions) {
      dispatch(getStaffQuarterPermissions({}));
    }
  }, [dispatch, sqLoading, sqPermissions]);

  const canViewModule = (moduleName: string) => {
    if (!sqPermissions || !Array.isArray(sqPermissions)) return true; // default to true if not loaded yet
    const perm = sqPermissions.find((p: any) => p.module === moduleName);
    return perm ? perm.canView !== false : true;
  };

  const stats = {
    pendingRequests: 2,
    activeAllotments: 1,
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="page-header mb-4">
          <div className="row align-items-center">
            <div className="col">
              <h3 className="page-title text-dark fw-bold">Staff Quarters Dashboard</h3>
              <ul className="breadcrumb bg-transparent p-0 mt-1">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item active">Staff Quarters</li>
              </ul>
            </div>
          </div>
        </div>


        <DashboardSection title="My Overview">
          {canViewModule("allotment") && (
            <DashboardTile
              title="My Allotments"
              icon="ti-home"
              count={stats.activeAllotments}
              link={routes.staffQuarter.myAllotments}
              colorClass="text-success"
            />
          )}
          <DashboardTile
            title="Pending Requests"
            icon="ti-clock"
            count={stats.pendingRequests}
            link={routes.staffQuarter.list}
            state={{ filterStatus: "PENDING" }}
            colorClass="text-warning"
          />
          {canViewModule("vacation") && (
            <DashboardTile
              title="Request Vacation"
              icon="ti-calendar-minus"
              link={routes.staffQuarter.requestVacation || routes.staffQuarter.vacationIntimation}
              state={{ showForm: true }}
              colorClass="text-info"
            />
          )}
          {canViewModule("vacation") && (
            <DashboardTile
              title="Vacation Intimation Form"
              icon="ti-file-text"
              link={routes.staffQuarter.vacationIntimationForm}
              colorClass="text-primary"
            />
          )}
        </DashboardSection>

        <DashboardSection title="Applications & Services">
          {canViewModule("registration") && (
            <DashboardTile
              title="New Registration"
              icon="ti-file-plus"
              link={routes.staffQuarter.newRegistration}
              colorClass="text-primary"
            />
          )}
          {canViewModule("occupation") && (
            <DashboardTile
              title="Occupation Report"
              icon="ti-key"
              link={routes.staffQuarter.occupation}
              state={{ showForm: true, activeTab: "occupation" }}
              colorClass="text-purple"
            />
          )}
          {canViewModule("occupation") && (
            <DashboardTile
              title="Withdrawal / Surrender"
              icon="ti-logout"
              link={routes.staffQuarter.occupation}
              state={{ showForm: true, activeTab: "withdrawal" }}
              colorClass="text-danger"
            />
          )}
          <DashboardTile
            title="All Staff Requests"
            icon="ti-list-check"
            link={routes.staffQuarter.list}
            state={{ filterStatus: "All Statuses" }}
            colorClass="text-secondary"
          />
        </DashboardSection>

        <DashboardSection title="Information & Help">
          <DashboardTile
            title="Quarter Rules"
            icon="ti-book"
            link="#"
            colorClass="text-dark"
          />
          <DashboardTile
            title="Maintenance Help"
            icon="ti-tool"
            link="#" // Placeholder
            colorClass="text-orange"
          />
          <DashboardTile
            title="Eligibility Calculator"
            icon="ti-calculator"
            link="#" // Placeholder
            colorClass="text-teal"
          />
        </DashboardSection>

      </div>

      <style>{`
        .hover-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.08) !important;
        }
        .text-purple { color: #6f42c1 !important; }
        .text-orange { color: #fd7e14 !important; }
        .text-teal { color: #20c997 !important; }
      `}</style>
    </div>
  );
};

export default Overview;
